import "leaflet/dist/leaflet.css";
import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import type { CollectionSite } from "./CollectionSites";

// ─── Geocoding ────────────────────────────────────────────────────────────────

type LatLng = { lat: number; lng: number };

const geocodeCache = new Map<string, LatLng | null>();

async function geocodeAddress(address: string): Promise<LatLng | null> {
  if (geocodeCache.has(address)) return geocodeCache.get(address)!;

  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`;
    const res = await fetch(url, {
      headers: { "User-Agent": "ConstructorFleet/1.0 (ua-dashboard)" },
    });
    if (!res.ok) {
      geocodeCache.set(address, null);
      return null;
    }
    const data = (await res.json()) as { lat?: string; lon?: string }[];
    const first = data[0];
    if (!first?.lat || !first?.lon) {
      geocodeCache.set(address, null);
      return null;
    }
    const result = { lat: parseFloat(first.lat), lng: parseFloat(first.lon) };
    geocodeCache.set(address, result);
    return result;
  } catch {
    geocodeCache.set(address, null);
    return null;
  }
}

type ResolvedSite = CollectionSite & { lat: number; lng: number };

function useResolvedSites(sites: CollectionSite[]) {
  const [resolved, setResolved] = useState<ResolvedSite[]>(() =>
    sites.filter((s): s is ResolvedSite => typeof s.lat === "number" && typeof s.lng === "number")
  );

  useEffect(() => {
    let active = true;

    // Seed immediately with any sites that already have coordinates.
    const withCoords = sites.filter(
      (s): s is ResolvedSite => typeof s.lat === "number" && typeof s.lng === "number"
    );
    setResolved(withCoords);

    // Geocode the rest in parallel (Nominatim allows ~1 req/s; stagger slightly).
    const needsGeocode = sites.filter(
      (s) => (typeof s.lat !== "number" || typeof s.lng !== "number") && s.address
    );

    if (needsGeocode.length === 0) return;

    needsGeocode.forEach((site, i) => {
      setTimeout(() => {
        void geocodeAddress(site.address!).then((coords) => {
          if (!active || !coords) return;
          setResolved((prev) => {
            if (prev.some((s) => s.id === site.id)) return prev;
            return [...prev, { ...site, ...coords }];
          });
        });
      }, i * 1100); // ~1 req/s to respect Nominatim's usage policy
    });

    return () => {
      active = false;
    };
  }, [sites]);

  return resolved;
}

const DEFAULT_ZOOM = 13;
const DEFAULT_CENTER: [number, number] = [39.7392, -104.9903]; // Denver fallback

function makePinIcon(open: boolean): L.DivIcon {
  const fill = open ? "#2d8a3e" : "#3b6bcc";
  const stroke = open ? "#1a5c28" : "#1e3f8a";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 8.25 12 24 12 24s12-15.75 12-24C24 5.37 18.63 0 12 0z"
      fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white" opacity="0.9"/>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: "",
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -38],
  });
}

const ICON_OPEN = makePinIcon(true);
const ICON_DEFAULT = makePinIcon(false);

type Props = {
  sites: CollectionSite[];
  centerLat?: number;
  centerLng?: number;
};

export function CollectionSiteMap({ sites, centerLat, centerLng }: Props) {
  const resolved = useResolvedSites(sites);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);

  const center: [number, number] =
    typeof centerLat === "number" && typeof centerLng === "number"
      ? [centerLat, centerLng]
      : (() => {
          const first = sites.find((s) => typeof s.lat === "number" && typeof s.lng === "number");
          return first ? ([first.lat!, first.lng!] as [number, number]) : DEFAULT_CENTER;
        })();

  // Initialize map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    mapRef.current = L.map(containerRef.current, {
      center,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: false,
    });

    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(mapRef.current);

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync markers whenever resolved sites change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const bounds: [number, number][] = [];

    for (const site of resolved) {
      const pos: [number, number] = [site.lat, site.lng];
      bounds.push(pos);

      const icon = site.open_now ? ICON_OPEN : ICON_DEFAULT;
      const marker = L.marker(pos, { icon });

      const lines: string[] = [`<strong>${site.name}</strong>`];
      if (site.address) lines.push(site.address);
      if (site.open_now) {
        lines.push('<span style="color:#2d8a3e;font-weight:600">Open now</span>');
      } else {
        lines.push("Closed / hours unknown");
      }
      if (site.approved === false) {
        lines.push('<span style="color:#c0392b">Not approved</span>');
      }

      marker.bindPopup(lines.join("<br>"));
      marker.addTo(map);
      markersRef.current.push(marker);
    }

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: DEFAULT_ZOOM });
    }
  }, [resolved]);

  // Re-center when the center prop changes
  useEffect(() => {
    mapRef.current?.setView(center, DEFAULT_ZOOM, { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center[0], center[1]]);

  const missingCoords = sites.length - resolved.length;

  return (
    <div>
      <div
        ref={containerRef}
        style={{ height: 360, width: "100%", borderRadius: 8, overflow: "hidden" }}
        aria-label="Collection sites map"
        role="region"
      />
      {missingCoords > 0 && (
        <p className="cf-help" style={{ margin: "0.4rem 0 0" }}>
          {missingCoords} site{missingCoords !== 1 ? "s are" : " is"} not shown — no coordinates on
          record.
        </p>
      )}
    </div>
  );
}
